import * as React from 'react';
import { IconButton as MatIconButton, IconButtonProps as MatIconButtonProps, SxProps, Theme } from '@mui/material';
import { hexToRGB, theme, customArcherColors } from '../../utils';
import { Icon, IconSize, IconKey } from '../Icon';
import { ButtonType } from '../../factories/button/button.types';

export type StylingProps = {
  iconColor?: string;
  activeColor?: string;
  hoverColor?: string;
};

const useStyles = (stylingProps: StylingProps): Record<string, SxProps<Theme>> => ({
  iconButtonRoot: {
    color: stylingProps.iconColor || 'primary.main',
    '&:hover': {
      borderRadius: '50%',
      backgroundColor: stylingProps.hoverColor
    },
    '&:focus': {
      backgroundColor: stylingProps.activeColor
    }
  }
});

export interface IIconButtonProps {
  actionType?: ButtonType;
  className?: string;
  color?: MatIconButtonProps['color'];
  component?: React.ElementType;
  dataProp?: string;
  disabled?: boolean;
  fontSize?: IconSize;
  icon?: IconKey;
  isActionIcon?: boolean;
  isExpansionPanelIcon?: boolean;
  id?: string;
  label?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  ref?: MatIconButtonProps['ref'];
  style?: object;
  sx?: SxProps<Theme>;
  tabIndex?: number;
  title?: string;
}

const IconButton = React.forwardRef<HTMLButtonElement | React.ReactInstance, IIconButtonProps>((props, ref) => {
  const { icon, label, fontSize = 'medium', dataProp = `${icon}-button`, isActionIcon, isExpansionPanelIcon, actionType, sx, ...rest } = props;

  const IconTrueColor =
    (isActionIcon && customArcherColors.getCustomActionIconColors(actionType)) ||
    (isExpansionPanelIcon && customArcherColors.getCustomExpansionPanelIconColors(actionType)) ||
    theme.palette.primary.main;

  const getActiveColor = IconTrueColor && hexToRGB(IconTrueColor, 0.15);
  const getHoverColor = IconTrueColor && hexToRGB(IconTrueColor, 0.07);

  const stylingProps = { iconColor: IconTrueColor, activeColor: getActiveColor, hoverColor: getHoverColor };
  const { iconButtonRoot } = useStyles(stylingProps);
  const sxProps = [iconButtonRoot, sx] as SxProps<Theme>;

  return (
    <MatIconButton sx={sxProps} aria-label={label} {...rest} ref={ref as any} data-selector={dataProp}>
      {icon && <Icon icon={icon} fontSize={fontSize} />}
    </MatIconButton>
  );
});

IconButton.displayName = 'IconButton';

export { IconButton };
